/**
 * 设备批量生成对话框
 * v8.0 新增：IoT 设备管理功能
 */
import { useState } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Checkbox } from "@/shared/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { DeviceApiService } from "@/modules/agent/services/device-api"
import { MAX_BATCH_COUNT, EXPIRES_OPTIONS } from "@/modules/agent/types/device"
import type { DeviceBatchCreateRequest, DevicePermissions } from "@/modules/agent/types/device"

interface DeviceCreateDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (batchNo: string) => void
}

/**
 * 设备批量生成对话框
 */
export function DeviceCreateDialog({
  projectId,
  open,
  onOpenChange,
  onSuccess,
}: DeviceCreateDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 表单状态
  const [orderNo, setOrderNo] = useState("")
  const [count, setCount] = useState("")
  const [prefix, setPrefix] = useState("")
  const [expiresInDays, setExpiresInDays] = useState("365")
  const [permissions, setPermissions] = useState<DevicePermissions>({
    wss: true,
    sse: true,
    server_vad: false,  // v8.3: 默认关闭云端 VAD（收费功能）
  })

  /**
   * 重置表单
   */
  const resetForm = () => {
    setOrderNo("")
    setCount("")
    setPrefix("")
    setExpiresInDays("365")
    setPermissions({ wss: true, sse: true, server_vad: false })
    setError(null)
  }

  /**
   * 验证表单
   */
  const validateForm = (): boolean => {
    if (!orderNo.trim()) {
      setError("请输入订单号")
      return false
    }
    const countNum = parseInt(count)
    if (isNaN(countNum) || countNum < 1) {
      setError("请输入有效的数量（最小为 1）")
      return false
    }
    if (countNum > MAX_BATCH_COUNT) {
      setError(`数量不能超过 ${MAX_BATCH_COUNT}`)
      return false
    }
    if (!permissions.wss && !permissions.sse) {
      setError("请至少选择一项权限")
      return false
    }
    setError(null)
    return true
  }

  /**
   * 提交表单
   */
  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    setError(null)

    try {
      const request: DeviceBatchCreateRequest = {
        order_no: orderNo.trim(),
        count: parseInt(count),
        permissions,
        expires_in_days: parseInt(expiresInDays),
      }
      if (prefix.trim()) {
        request.device_id_prefix = prefix.trim()
      }

      const response = await DeviceApiService.batchCreate(projectId, request)
      resetForm()
      onSuccess(response.data.batch_no)
    } catch (err: unknown) {
      console.error("批量生成失败:", err)
      if (err instanceof Error) {
        setError(err.message || "批量生成失败，请重试")
      } else {
        setError("批量生成失败，请重试")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * 关闭对话框
   */
  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>批量生成设备</DialogTitle>
          <DialogDescription>
            为项目批量生成 IoT 设备凭证，生成后可下载 Excel 文件
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* 订单号 */}
          <div className="grid gap-2">
            <Label htmlFor="orderNo">订单号 *</Label>
            <Input
              id="orderNo"
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              placeholder="例如: ORD-2025-001234"
            />
            <p className="text-xs text-slate-500">
              用于标识设备批次，方便后续按订单管理
            </p>
          </div>

          {/* 数量 */}
          <div className="grid gap-2">
            <Label htmlFor="count">生成数量 *</Label>
            <Input
              id="count"
              type="number"
              min={1}
              max={MAX_BATCH_COUNT}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder={`1 - ${MAX_BATCH_COUNT}`}
            />
            <p className="text-xs text-slate-500">
              单次最多生成 {MAX_BATCH_COUNT} 个设备
            </p>
          </div>

          {/* 设备 ID 前缀 */}
          <div className="grid gap-2">
            <Label htmlFor="prefix">设备 ID 前缀</Label>
            <Input
              id="prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="例如: MIRROR_"
            />
            <p className="text-xs text-slate-500">
              可选，设备 ID 格式为: 前缀 + 4位序号，如 MIRROR_0001
            </p>
          </div>

          {/* 有效期 */}
          <div className="grid gap-2">
            <Label>有效期</Label>
            <Select value={expiresInDays} onValueChange={setExpiresInDays}>
              <SelectTrigger>
                <SelectValue placeholder="选择有效期" />
              </SelectTrigger>
              <SelectContent>
                {EXPIRES_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 权限 */}
          <div className="grid gap-2">
            <Label>访问权限 *</Label>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="wss"
                  checked={permissions.wss}
                  onCheckedChange={(checked) => 
                    setPermissions(prev => ({ ...prev, wss: !!checked }))
                  }
                />
                <label
                  htmlFor="wss"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  语音通信 (WSS)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sse"
                  checked={permissions.sse}
                  onCheckedChange={(checked) => 
                    setPermissions(prev => ({ ...prev, sse: !!checked }))
                  }
                />
                <label
                  htmlFor="sse"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  文字通信 (SSE)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="server_vad"
                  checked={permissions.server_vad ?? false}
                  onCheckedChange={(checked) => 
                    setPermissions(prev => ({ ...prev, server_vad: !!checked }))
                  }
                />
                <label
                  htmlFor="server_vad"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  云端 VAD <span className="text-amber-500 text-xs">(收费)</span>
                </label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              云端 VAD：由云端检测语音结束，消耗阿里云 ASR 资源
            </p>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            生成设备
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
